import { Alert, Divider, Stack } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import {
  useGetTransactionQuery,
  useRefundPaymentMutation,
} from 'api/payments/payments';
import { STATUSES } from 'appConstants';
import FormDrawer from 'components/drawers/form-drawer/form-drawer.component';
import { IncDrawerType } from 'components/drawers/inc-drawer/inc-drawer.component';
import { getRefundableAmount } from 'pages/payments/transactions/transactions.utils';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import RefundForm from '../../../forms/refund-form/refund-form.component';
import { useRefundDrawerForm } from '../../../forms/refund-form/refund-form.hooks';
import OrderDetails from '../order-details/order-details.component';
import OrderTotalInfo from '../order-total-info/order-total-info.component';
import RefundTotalInfo from '../refund-total-info/refund-total-info.component';
import GiftCardDetails from './giftcard-details-drawer/giftcard-details-drawer.component';
import { useGetGiftCardQuery } from 'api/giftcard-service/giftcard-service';
import { useSnackbar } from 'notistack';

export type RefundTransactionDrawerDataType = string;

type RefundTransactionDrawerProps =
  IncDrawerType<RefundTransactionDrawerDataType>;

const RefundTransactionDrawer = ({
  close,
  data: drawerData,
  isOpen,
}: RefundTransactionDrawerProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isFetching } = useGetTransactionQuery(drawerData ?? skipToken);
  const { chargeAmount, refunds, currency, status, giftCardId } = data ?? {};
  const cardNumber = giftCardId;
  const {
    data: giftCardData,
    refetch,
    isFetching: getGiftCardDetailsFetching,
  } = useGetGiftCardQuery(cardNumber, { skip: !giftCardId });
  const { cardAccountBalance } = giftCardData ?? {};
  const cardAccountBalanceValue = cardAccountBalance * 1000;

  const useCurrentCardBalance =
    cardAccountBalanceValue < getRefundableAmount(chargeAmount, refunds);

  const refundableAmountValue = useCurrentCardBalance
    ? cardAccountBalanceValue
    : getRefundableAmount(chargeAmount, refunds);

  const { form } = useRefundDrawerForm({
    chargeAmount,
    refundableAmount: refundableAmountValue,
    currency,
  });

  useEffect(() => {
    if (isOpen) refetch();
  }, [isOpen, refetch]);

  const { reset, clearErrors } = form;
  const [refundPaymentMutation, { isSuccess, isLoading }] =
    useRefundPaymentMutation();

  const handleClose = useCallback(() => {
    close();
    reset();
    clearErrors();
  }, [clearErrors, close, reset]);

  const onValid = ({
    refundAmount,
    isPartialRefund: isPartialRefundSelected,
    ...rest
  }) => {
    const isPartialRefund =
      isPartialRefundSelected || status === STATUSES.PARTIALLY_REFUNDED;

    const body = {
      ...rest,
      isPartialRefund,
      refundAmount: isPartialRefund ? refundAmount * 1000 : undefined,
      type: data.type,
      orderId: data.orderId,
      giftcardTransactionId: data.giftcardTxId,
      extPaymentTransactionId: data.transactionId,
    };
    refundPaymentMutation(body);
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar(t('drawer.refundDrawer.refundAction.success'), {
        severity: 'success',
      });
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <FormDrawer
      title={t('drawer.refundDrawer.title')}
      open={isOpen}
      onValid={onValid}
      cancel={handleClose}
      isLoading={isLoading || isFetching || getGiftCardDetailsFetching}
      formMethods={form}
      data-testid="RefundTransactionDrawer.FormDrawer"
      onClose={handleClose}
      submitButtonLabel={t('drawer.refundDrawer.refundAction.submit')}
    >
      <Stack spacing={2} divider={<Divider />}>
        <OrderDetails items={data?.items} currency={data?.currency} />

        <OrderTotalInfo
          orderTotal={data?.orderTotal}
          chargeAmount={data?.chargeAmount}
          currency={data?.currency}
        />

        {data?.refunds?.length > 0 && (
          <RefundTotalInfo
            refunds={data?.refunds}
            currency={data?.currency}
            chargeAmount={data?.chargeAmount}
          />
        )}

        {giftCardId && (
          <GiftCardDetails
            giftCardNumber={giftCardId}
            currentCardBalance={cardAccountBalance?.toString()}
            currency={currency}
          />
        )}

        <Alert severity="info" icon={false} elevation={2}>
          <RefundForm
            transaction={data}
            refundableAmountValue={refundableAmountValue}
          />
        </Alert>
      </Stack>
    </FormDrawer>
  );
};

export default RefundTransactionDrawer;
